import { useEffect, useState } from 'react';
import type { Participacao } from '@/types/evento-publico';
import { cidadesDosGrupos, dividirNomeGrupo } from '@/lib/grupoOpcao';
import { textoDoValor, vagasRestantes } from '../utils';
import { aceitaComoSuplente, type ConfigModalidade } from './EscolherParticipacao';

interface GruposDuasEtapasProps {
  lista: Participacao[];
  selecionada: string | null;
  onSelecionar: (chave: string) => void;
  vendeIngressos: boolean;
  desabilitado: boolean;
  /** Mostra "+ taxa de serviço" quando o comprador paga a taxa. */
  mostraTaxa?: boolean;
  /** aceita_suplentes / situacao_visivel por modalidade. */
  configPorModalidade?: Map<number, ConfigModalidade>;
}

function chaveDe(p: Participacao): string {
  return `${p.modalidade_id}:${p.opcao_id ?? ''}`;
}

// Grupos no formato "Cidade · Sexo": a pessoa escolhe a cidade e depois o sexo.
export default function GruposDuasEtapas({
  lista,
  selecionada,
  onSelecionar,
  vendeIngressos,
  desabilitado,
  mostraTaxa = false,
  configPorModalidade,
}: GruposDuasEtapasProps) {
  const cidades = cidadesDosGrupos(lista);

  const [cidade, setCidade] = useState<string | null>(() => {
    const sel = lista.find((p) => chaveDe(p) === selecionada);
    return dividirNomeGrupo(sel?.grupo ?? null)?.cidade ?? null;
  });

  useEffect(() => {
    const sel = lista.find((p) => chaveDe(p) === selecionada);
    const cidadeDoGrupo = dividirNomeGrupo(sel?.grupo ?? null)?.cidade ?? null;
    if (cidadeDoGrupo && cidadeDoGrupo !== cidade) setCidade(cidadeDoGrupo);
  }, [lista, selecionada, cidade]);

  const opcoes = cidade
    ? lista.filter((p) => dividirNomeGrupo(p.grupo)?.cidade === cidade)
    : [];

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[13px] font-bold text-foreground-800">
          De onde você está fazendo a ficha?
        </p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {cidades.map((c) => {
            const ativa = cidade === c;
            return (
              <button
                key={c}
                type="button"
                disabled={desabilitado}
                onClick={() => setCidade(c)}
                className={`aperta flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[14px] border-2 px-3 py-3 text-[14px] font-bold transition-colors ${
                  ativa
                    ? 'border-primary-500 bg-primary-50 text-foreground-950'
                    : 'cursor-pointer border-background-200 bg-background-50 text-foreground-800'
                } ${desabilitado ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {c}
                {ativa && <i className="ri-check-line text-sm text-primary-600" aria-hidden />}
              </button>
            );
          })}
        </div>
      </div>

      {cidade && (
        <div className="space-y-2">
          <p className="text-[13px] font-bold text-foreground-800">Masculino ou Feminino?</p>
          {opcoes.map((p) => {
            const cfg = configPorModalidade?.get(p.modalidade_id) ?? null;
            const suplente = aceitaComoSuplente(p, cfg);
            const escondido = suplente && cfg?.situacao_visivel === false;
            const ativo = (p.situacao === 'abertas' || suplente) && !desabilitado;
            const selecionado = ativo && selecionada === chaveDe(p);
            const vagas = vagasRestantes(p.restantes, vendeIngressos);
            const sexo = dividirNomeGrupo(p.grupo)?.sexo ?? p.grupo;

            return (
              <button
                key={p.opcao_id ?? p.modalidade_id}
                type="button"
                disabled={!ativo}
                onClick={() => ativo && onSelecionar(chaveDe(p))}
                className={`aperta flex w-full items-center justify-between gap-3 rounded-[18px] border-2 p-4 text-left transition-colors ${
                  selecionado
                    ? 'border-primary-500 bg-primary-50'
                    : ativo
                      ? 'cursor-pointer border-background-200 bg-background-50 shadow-pcard'
                      : 'cursor-not-allowed border-background-200 bg-background-100 opacity-60'
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-bold text-foreground-950">{sexo}</p>
                  <p className="mt-0.5 truncate text-[14px] font-semibold text-foreground-600">
                    {textoDoValor(p)}
                  </p>
                  {mostraTaxa && ativo && (
                    <p className="mt-0.5 text-[12px] font-medium text-foreground-500">
                      + taxa de serviço
                    </p>
                  )}
                  {ativo && vagas && (
                    <p className="mt-1 text-[12px] font-semibold text-warning-600">{vagas}</p>
                  )}
                  {suplente && !escondido && (
                    <p className="mt-1 text-[12px] font-semibold text-warning-600">
                      Vagas esgotadas · entre como suplente
                    </p>
                  )}
                  {!ativo && (
                    <p className="mt-1 text-[12px] font-medium text-foreground-400">
                      {p.situacao === 'em_breve'
                        ? 'Em breve'
                        : p.situacao === 'esgotado'
                          ? 'Esgotado'
                          : 'Encerradas'}
                    </p>
                  )}
                </div>

                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${
                    selecionado
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-background-300 text-transparent'
                  }`}
                >
                  <i className="ri-check-line text-sm" aria-hidden />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}